import { CatalogCategory, CatalogCompany, CatalogItem } from '@/shared/types/catalog';
import './index.less';
import { useQueryParams } from '@/shared/hooks';
import { ParamKey } from '@/shared/types/url-params';
import { SupplierProfileProductsSort } from '@/shared/types/sourcing-marketplace';
import { isEmpty, size } from 'lodash-es';
import CatalogItemMinifiedCard from '../../Common/CatalogItemMinifiedCard';
import { useEffect, useMemo, useState } from 'react';
import { getCatalogItems } from '@/lib/service/catalog';
import {
  SUPPLIER_PRODUCTS_RECOMBEE_PAGE_SIZE,
  SUPPLIER_PRODUCTS_SEARCH_PAGE_SIZE,
} from '@/shared/constants/sourcing-marketplace';
import { Pagination } from 'antd';
import SearchBar from './SearchBar';
import CategoryFilter from './CategoryFilter';
import SortFilter from './SortFilter';
import CreateCustomProductCard from '../../Common/CreateCustomProductCard';

interface ProdustcsTabProps {
  products: CatalogItem[];
  L3Categories: CatalogCategory[];
  page: number;
  total: number;
  supplier: CatalogCompany;
}

const ProductsTab = ({ products, L3Categories, page, total, supplier }: ProdustcsTabProps) => {
  const { queryParams, setQueryParams } = useQueryParams<{}>();
  const sort = queryParams.get(ParamKey.SORT) ?? SupplierProfileProductsSort.FEATURED;
  const searchKey = queryParams.get(ParamKey.SEARCH);
  const isRecombee = sort === SupplierProfileProductsSort.FEATURED && isEmpty(searchKey);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [skeletonItems] = useState(Array(30).fill(null));
  const [productsList, setProductsList] = useState<CatalogItem[]>();
  const [currentPage, setCurrentPage] = useState<number>(page);
  const [ifShowLoadMoreBtn, setIfShowLoadMoreBtn] = useState<boolean>(false);
  const ifShowSearchPaginator = useMemo(() => {
    return total >= SUPPLIER_PRODUCTS_SEARCH_PAGE_SIZE;
  }, [total]);

  useEffect(() => {
    setIfShowLoadMoreBtn(size(products) === SUPPLIER_PRODUCTS_RECOMBEE_PAGE_SIZE);
    setProductsList(products);
    setCurrentPage(page);
  }, [products, page]);

  const loadMoreProducts = async () => {
    setIsLoading(true);
    const nextPage = currentPage + 1;
    const { list } = await getCatalogItems({
      catalogCompanyId: [supplier.id],
      page: nextPage,
      size: SUPPLIER_PRODUCTS_RECOMBEE_PAGE_SIZE,
    });

    setIsLoading(false);
    setProductsList([...productsList, ...list]);
    setCurrentPage(nextPage);
    setIfShowLoadMoreBtn(size(list) === SUPPLIER_PRODUCTS_RECOMBEE_PAGE_SIZE);
  };

  const loadMoreSearchProducts = (page) => {
    setQueryParams({ [ParamKey.PRODUCTS_SEARCH_PAGE]: page - 1 });
  };

  const renderLoadMoreBtn = () => {
    return (
      <div className="recombee-load-more-box">
        <div className="skeleton-box">
          {skeletonItems.map((item, index) => (
            <div className="skeleton-item" key={`skeleton-${index}`} />
          ))}
        </div>
        <span className="show-more" onClick={loadMoreProducts}>
          See more products
        </span>
      </div>
    );
  };

  const renderSearchPagination = () => {
    return (
      <div className="pagination-box">
        <Pagination
          defaultCurrent={page + 1}
          total={total}
          pageSize={SUPPLIER_PRODUCTS_SEARCH_PAGE_SIZE}
          onChange={loadMoreSearchProducts}
          showSizeChanger={false}
        />
      </div>
    );
  };

  const renderFilter = () => {
    return (
      <div className="products-filter-box">
        <SearchBar />
        <div className="divider" />
        <SortFilter />
        <div className="divider" />
        <CategoryFilter L3Categories={L3Categories} />
      </div>
    );
  };

  return (
    <div className="supplier-products-box">
      {renderFilter()}
      {productsList && productsList?.length > 0 ? (
        <div className="product-list">
          {productsList.map((product) => (
            <CatalogItemMinifiedCard key={product?.id} product={product} />
          ))}
          {((isRecombee && !ifShowLoadMoreBtn) || (!isRecombee && !ifShowSearchPaginator)) && (
            <CreateCustomProductCard supplier={supplier} />
          )}
        </div>
      ) : (
        <div className="product-list">
          <CreateCustomProductCard supplier={supplier} />
        </div>
      )}

      {isRecombee && !isLoading && ifShowLoadMoreBtn && renderLoadMoreBtn()}
      {!isRecombee && !isLoading && ifShowSearchPaginator && renderSearchPagination()}
    </div>
  );
};

export default ProductsTab;
